import {
  DistrictDataByType,
  DistrictDataBySchool,
  ReportingMixpanelEvent,
} from '@peekapak/types/ReportDataExchangeTypes';
import { SchoolModel } from '@peekapak/types/DataProtocolTypes';
import { EventName } from '@peekapak/types/AnalyticsTypes';
import { noWhiteSpace } from '../../GlobalFunctions';
import { moodMap as elementaryMoodIndexMap } from '../../ClassroomDashboard/PeekavilleDataHelpers';
import { values as secondaryMoodWheel } from '../../CheckIn/checkInValues';
import { getSchoolsEventFilter, lookupNewDBStartDate, resolveActivityId } from '../reportUtilities';
import {
  ELEMENTARY_MOODS,
  SECONDARY_MOOD_CATEGORIES,
  getActivityDisplayName,
  monthKeyOf,
  monthLabelOf,
  monthsInRange,
  resolveStoryName,
} from '../SchoolReport/schoolReportDeck';

//////////////////////////////////////////////////////////////////
//
// PDF-only Mixpanel query
//
// The on-page district report only needs logins/lessons/story-open counts, so its query projects
// a handful of fields per event. The PDF additionally needs moods, games and stories.
// Rather than widen the page's own query (every district event would carry the extra fields, and
// that response is already the one closest to the API Gateway 29s / Lambda 6MB response limits),
// this separate query only selects the handful of event types the PDF needs and has Mixpanel
// group them server-side, so the response is a few hundred rows of counts instead of raw events.
//
//////////////////////////////////////////////////////////////////

// Same list as getNewStoryOpenEvents (reportUtilities.ts). LESSONS_BOOK_OPEN isn't actually defined
// on EventName (so it's undefined there too) - it's filtered out here because an event selector
// without an event name would match every event in the district.
const STORY_OPEN_EVENT_NAMES: string[] = [
  EventName.JOURNAL_BOOK_OPEN,
  EventName.JOURNAL_ASSIGNMENT_STORY_OPEN,
  EventName.LESSONS_STORY_OPEN,
  (EventName as Record<string, string | undefined>).LESSONS_BOOK_OPEN,
].filter((name): name is string => !!name);

const PDF_EVENT_NAMES: string[] = [
  EventName.JOURNAL_MOOD_LOG,
  EventName.JOURNAL_QUEST_COMPLETE,
  ...STORY_OPEN_EVENT_NAMES,
];

// Raw check-in mood -> deck mood/category, mirroring resolveElementaryMood/resolveSecondaryCategory
// (schoolReportDeck.ts). Built from the same source constants and inlined into the JQL, so the
// server-side bucketing can't drift from what the school deck counts.
const elementaryMoodLookup: Record<string, string> = Object.fromEntries([
  ...ELEMENTARY_MOODS.map((mood) => [mood, mood]),
  ...elementaryMoodIndexMap
    .map((mood, index) => [String(index), mood])
    .filter(([, mood]) => ELEMENTARY_MOODS.includes(mood)),
]);
const secondaryMoodLookup: Record<string, string> = Object.fromEntries(
  secondaryMoodWheel.flatMap((category) => category.subEmotions.map((sub) => [sub, category.title])),
);

// The earliest date the unified Mixpanel project has data for. Anything older lives in the legacy
// projects / DynamoDB, which this query doesn't cover (see getDistrictPdfQueryRange).
const UNIFIED_DB_START = lookupNewDBStartDate('startDate');

export const getDistrictPdfQueryRange = (startDate: Date, endDate: Date): { from: string; to: string } | null => {
  if (endDate < UNIFIED_DB_START) return null;
  const from = startDate < UNIFIED_DB_START ? UNIFIED_DB_START : startDate;
  return { from: from.toISOString().substring(0, 10), to: endDate.toISOString().substring(0, 10) };
};

// Every emitted row is padded to the same width so the groupBy key selectors can index into it.
const ROW_WIDTH = 6;

// `schools` is every school on the district's list (each its id, or its combineIds) - events are
// selected by school, with the same filter as the on-page report, rather than by their district tag.
export function getDistrictPdfAggregatesScript(startDate: string, endDate: string, schools: Array<string | string[]>) {
  const eventSelectors = PDF_EVENT_NAMES.map((event) => ({ event }));
  const keySelectors = Array.from({ length: ROW_WIDTH }, (_, i) => `function(row) { return row[${i}]; }`).join(', ');

  return noWhiteSpace`
    function main() {
      var ELEMENTARY = ${JSON.stringify(elementaryMoodLookup)};
      var SECONDARY = ${JSON.stringify(secondaryMoodLookup)};
      var STORY_EVENTS = ${JSON.stringify(STORY_OPEN_EVENT_NAMES)};
      var WIDTH = ${String(ROW_WIDTH)};
      function pad(row) {
        var out = [];
        for (var i = 0; i < WIDTH; i++) {
          var value = row[i];
          out.push(value === undefined || value === null ? '' : String(value));
        }
        return out;
      }
      function monthKey(time) {
        var d = new Date(time);
        return d.getUTCFullYear() + '-' + ('0' + (d.getUTCMonth() + 1)).slice(-2);
      }
      return Events({
        from_date: '${startDate}',
        to_date: '${endDate}',
        event_selectors: ${JSON.stringify(eventSelectors)}
      })
      .filter(function(event) {
        return (${getSchoolsEventFilter('schoolId', schools, true)});
      })
      .map(function(event) {
        var p = event.properties;
        var rows = [];
        if (event.name === '${EventName.JOURNAL_MOOD_LOG}') {
          if (p['userType'] !== 'Student' || p['mood'] === undefined || p['mood'] === null) return rows;
          rows.push(['checkin']);
          var grade = parseInt(String(p['grade'] === undefined || p['grade'] === null ? '' : p['grade']), 10);
          if (isNaN(grade)) return rows;
          var band = grade <= 5 ? 'elementary' : 'secondary';
          var mood = band === 'elementary' ? ELEMENTARY[String(p['mood'])] : SECONDARY[String(p['mood'])];
          if (!mood) return rows;
          rows.push(['mood', band, monthKey(event.time), mood]);
          rows.push(['moodUser', band, p['userId'] || event.distinct_id]);
        } else if (event.name === '${EventName.JOURNAL_QUEST_COMPLETE}') {
          var completion = Number(p['completionPercent'] === undefined || p['completionPercent'] === null ? 100 : p['completionPercent']);
          if (completion > 0) rows.push(['quest', p['quest'], p['gameId']]);
        } else if (STORY_EVENTS.indexOf(event.name) >= 0) {
          rows.push(['story', event.name, p['book'], p['unit'], p['story'], p['quest']]);
        }
        return rows.map(pad);
      })
      .flatten()
      .groupBy([${keySelectors}], mixpanel.reducer.count());
    }
  `;
}

export interface DistrictPdfAggregates {
  checkins: number;
  elementaryByMonth: Record<string, Record<string, number>>;
  secondaryByMonth: Record<string, Record<string, number>>;
  elementaryStudents: number;
  secondaryStudents: number;
  // display name -> completed plays
  games: Record<string, number>;
  // display name -> opens
  stories: Record<string, number>;
}

export interface DistrictPdfAggregateRow {
  key: string[];
  value: number;
}

export const emptyDistrictPdfAggregates = (): DistrictPdfAggregates => ({
  checkins: 0,
  elementaryByMonth: {},
  secondaryByMonth: {},
  elementaryStudents: 0,
  secondaryStudents: 0,
  games: {},
  stories: {},
});

export const parseDistrictPdfAggregates = (rows: DistrictPdfAggregateRow[]): DistrictPdfAggregates => {
  const result = emptyDistrictPdfAggregates();
  const bump = (map: Record<string, number>, key: string, by: number) => {
    map[key] = (map[key] || 0) + by;
  };

  (rows || []).forEach(({ key, value }) => {
    const [kind, a, b, c, d, e] = key || [];
    const count = Number(value) || 0;
    switch (kind) {
      case 'checkin':
        result.checkins += count;
        break;
      case 'mood': {
        const byMonth = a === 'elementary' ? result.elementaryByMonth : result.secondaryByMonth;
        if (!byMonth[b]) byMonth[b] = {};
        bump(byMonth[b], c, count);
        break;
      }
      case 'moodUser':
        // One row per distinct (band, student) - the row's count is that student's check-ins.
        if (a === 'elementary') result.elementaryStudents += 1;
        else result.secondaryStudents += 1;
        break;
      case 'quest': {
        // Same naming path as the school deck's Mixpanel game-progress map
        // (aggregateGameProgressByStudent -> resolveActivityId -> getActivityDisplayName).
        const activityId = resolveActivityId({ name: EventName.JOURNAL_QUEST_COMPLETE, quest: a, gameId: b } as never);
        bump(result.games, getActivityDisplayName(activityId), count);
        break;
      }
      case 'story': {
        const name = resolveStoryName({
          name: a,
          book: b || undefined,
          unit: c || undefined,
          story: d || undefined,
          quest: e || undefined,
        });
        bump(result.stories, name, count);
        break;
      }
      default:
        break;
    }
  });

  return result;
};

//////////////////////////////////////////////////////////////////
//
// Deck metrics
//
//////////////////////////////////////////////////////////////////

export interface SchoolSeries {
  name: string;
  data: number[];
  total: number;
}

export interface DistrictDeckMetrics {
  districtName: string;
  dateRangeLabel: string;
  overview: {
    staff: number;
    students: number;
    lessons: number;
    activities: number;
    stories: number;
    checkins: number;
    assignments: number;
  };
  monthLabels: string[];
  educatorLoginsTotal: number;
  studentLoginsTotal: number;
  educatorLoginsBySchool: SchoolSeries[];
  studentLoginsBySchool: SchoolSeries[];
  educatorDayOfWeek: { day: string; count: number }[];
  mostUsedDay: string;
  topEducatorsBySchool: { schoolName: string; educators: { name: string; logins: number }[] }[];
  elementaryMoods: { mood: string; percent: number }[];
  elementaryUpMoodPercent: number;
  elementaryStudents: number;
  elementaryMoodsByMonth: { month: string; counts: Record<string, number> }[];
  secondaryMoods: { mood: string; percent: number }[];
  secondaryUpMoodPercent: number;
  secondaryStudents: number;
  secondaryMoodsByMonth: { month: string; counts: Record<string, number> }[];
  topGames: { name: string; plays: number }[];
  mostPopularGame: string;
  topStories: { name: string; reads: number }[];
}

export interface DistrictDeckMetricsInput {
  districtName: string;
  startDate: Date | null;
  endDate: Date | null;
  schoolList: SchoolModel[];
  dataByType: DistrictDataByType;
  // The on-page table rows - the overview's staff/lessons/stories numbers are its column sums, so
  // the PDF always agrees with the TOTAL row the user is looking at.
  dataBySchool: DistrictDataBySchool;
  aggregates: DistrictPdfAggregates;
  // Sum of each school's own report count (loadDistrictAssignmentsCount).
  assignments: number;
  // userId -> display name, for the top-educator tables
  educatorNames: Record<string, string>;
}

const DAY_NAMES = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const WEEKDAYS_IN_ORDER = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const ELEMENTARY_UP_MOODS = ['Excited', 'Happy', 'Calm'];
const SECONDARY_UP_MOODS = ['Surprised', 'Confident', 'Happy', 'Calm'];

// Splits events into the same buckets the on-page table uses (getNewDistrictDataBySchool's
// filterBySchool): each school's own id or its combineIds, plus an "Other" bucket for events with
// no schoolId at all. Keeping the identical rule means every per-school number in the PDF matches
// the corresponding table row.
export const groupEventsBySchool = (
  events: ReportingMixpanelEvent[],
  schoolList: SchoolModel[],
): { name: string; events: ReportingMixpanelEvent[] }[] => {
  const buckets = schoolList.map((school) => ({
    name: school.name,
    ids: new Set(school.combineIds?.length ? school.combineIds : [school.id]),
    events: [] as ReportingMixpanelEvent[],
  }));
  const other = { name: 'Other', events: [] as ReportingMixpanelEvent[] };

  (events || []).forEach((event) => {
    if (event.schoolId === undefined) {
      other.events.push(event);
      return;
    }
    // A school id can legitimately appear in more than one school's combineIds - count it under
    // each, same as filterBySchool does for each table row.
    buckets.forEach((bucket) => {
      if (bucket.ids.has(event.schoolId)) bucket.events.push(event);
    });
  });

  return [...buckets.map(({ name, events }) => ({ name, events })), other];
};

const toSeries = (events: ReportingMixpanelEvent[], schoolList: SchoolModel[], monthKeys: string[]): SchoolSeries[] =>
  groupEventsBySchool(events, schoolList)
    .map(({ name, events: schoolEvents }) => {
      const byMonth: Record<string, number> = {};
      schoolEvents.forEach((event) => {
        const key = monthKeyOf(event.time);
        byMonth[key] = (byMonth[key] || 0) + 1;
      });
      const data = monthKeys.map((key) => byMonth[key] || 0);
      return { name, data, total: data.reduce((sum, n) => sum + n, 0) };
    })
    .filter((series) => series.total > 0)
    .sort((a, b) => b.total - a.total);

const summarizeMoods = (
  byMonth: Record<string, Record<string, number>>,
  order: string[],
  upMoods: string[],
  monthKeys: string[],
) => {
  const totals: Record<string, number> = Object.fromEntries(order.map((mood) => [mood, 0]));
  monthKeys.forEach((month) => {
    order.forEach((mood) => {
      totals[mood] += byMonth[month]?.[mood] || 0;
    });
  });
  const total = order.reduce((sum, mood) => sum + totals[mood], 0);
  const upTotal = upMoods.reduce((sum, mood) => sum + totals[mood], 0);
  return {
    moods: order.map((mood) => ({ mood, percent: total ? Math.round((totals[mood] / total) * 100) : 0 })),
    upMoodPercent: total ? Math.round((upTotal / total) * 100) : 0,
    byMonth: monthKeys.map((month) => ({
      month: monthLabelOf(month),
      counts: Object.fromEntries(order.map((mood) => [mood, byMonth[month]?.[mood] || 0])),
    })),
  };
};

const topEntries = (counts: Record<string, number>, limit: number) =>
  Object.entries(counts)
    .map(([name, value]) => ({ name, value }))
    .sort((a, b) => b.value - a.value)
    .slice(0, limit);

const sumColumn = (rows: DistrictDataBySchool, column: keyof Omit<DistrictDataBySchool[number], 'school_name'>) =>
  (rows || []).reduce((sum, row) => sum + (Number(row[column]) || 0), 0);

export const computeDistrictDeckMetrics = (input: DistrictDeckMetricsInput): DistrictDeckMetrics => {
  const {
    districtName,
    startDate,
    endDate,
    schoolList,
    dataByType,
    dataBySchool,
    aggregates,
    assignments,
    educatorNames,
  } = input;
  const educatorLogins = dataByType.educator_logins || [];
  const studentLogins = dataByType.student_logins || [];

  const rangeMonthKeys = monthsInRange(startDate, endDate);
  const monthKeys = rangeMonthKeys.length
    ? rangeMonthKeys
    : [...new Set([...educatorLogins, ...studentLogins].map((event) => monthKeyOf(event.time)))].sort();

  const educatorLoginsBySchool = toSeries(educatorLogins, schoolList, monthKeys);
  const studentLoginsBySchool = toSeries(studentLogins, schoolList, monthKeys);

  // Day-of-week and top educators use the same per-school buckets as the charts above, so an event
  // the table/chart doesn't attribute to any school isn't counted here either.
  const groupedEducatorLogins = groupEventsBySchool(educatorLogins, schoolList);
  const dayCounts = Array(7).fill(0) as number[];
  groupedEducatorLogins.forEach(({ events }) =>
    events.forEach((event) => {
      dayCounts[new Date(event.time).getUTCDay()]++;
    }),
  );
  const educatorDayOfWeek = WEEKDAYS_IN_ORDER.map((day) => ({ day, count: dayCounts[DAY_NAMES.indexOf(day)] })).filter(
    (d) => d.count > 0,
  );
  const mostUsedDay = educatorDayOfWeek.length
    ? educatorDayOfWeek.reduce((best, d) => (d.count > best.count ? d : best)).day
    : 'N/A';

  const topEducatorsBySchool = groupedEducatorLogins
    .map(({ name, events }) => {
      const loginsByUser: Record<string, number> = {};
      events.forEach((event) => {
        const id = event.userId || event.distinct_id;
        if (id) loginsByUser[id] = (loginsByUser[id] || 0) + 1;
      });
      // Only educators we can name - like the school deck, which lists rostered teachers only.
      const educators = Object.entries(loginsByUser)
        .filter(([id]) => educatorNames[id])
        .map(([id, logins]) => ({ name: educatorNames[id], logins }))
        .sort((a, b) => b.logins - a.logins)
        .slice(0, 5);
      return { schoolName: name, educators, total: events.length };
    })
    .filter((school) => school.educators.length > 0)
    .sort((a, b) => b.total - a.total)
    .map(({ schoolName, educators }) => ({ schoolName, educators }));

  const elementary = summarizeMoods(aggregates.elementaryByMonth, ELEMENTARY_MOODS, ELEMENTARY_UP_MOODS, monthKeys);
  const secondary = summarizeMoods(
    aggregates.secondaryByMonth,
    SECONDARY_MOOD_CATEGORIES,
    SECONDARY_UP_MOODS,
    monthKeys,
  );

  const topGames = topEntries(aggregates.games, 10).map(({ name, value }) => ({ name, plays: value }));
  const topStories = topEntries(aggregates.stories, 10).map(({ name, value }) => ({ name, reads: value }));

  const studentIds = new Set(studentLogins.map((event) => event.userId || event.distinct_id).filter(Boolean));
  const formatDate = (date: Date | null) =>
    date ? date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' }) : '';

  return {
    districtName,
    dateRangeLabel: `${formatDate(startDate)} - ${formatDate(endDate)}`,
    overview: {
      staff: sumColumn(dataBySchool, 'number_of_educators'),
      students: studentIds.size,
      lessons: sumColumn(dataBySchool, 'lessons_accessed'),
      activities: Object.values(aggregates.games).reduce((sum, n) => sum + n, 0),
      stories: sumColumn(dataBySchool, 'story_opens'),
      checkins: aggregates.checkins,
      assignments,
    },
    monthLabels: monthKeys.map(monthLabelOf),
    educatorLoginsTotal: educatorLoginsBySchool.reduce((sum, s) => sum + s.total, 0),
    studentLoginsTotal: studentLoginsBySchool.reduce((sum, s) => sum + s.total, 0),
    educatorLoginsBySchool,
    studentLoginsBySchool,
    educatorDayOfWeek,
    mostUsedDay,
    topEducatorsBySchool,
    elementaryMoods: elementary.moods,
    elementaryUpMoodPercent: elementary.upMoodPercent,
    elementaryStudents: aggregates.elementaryStudents,
    elementaryMoodsByMonth: elementary.byMonth,
    secondaryMoods: secondary.moods,
    secondaryUpMoodPercent: secondary.upMoodPercent,
    secondaryStudents: aggregates.secondaryStudents,
    secondaryMoodsByMonth: secondary.byMonth,
    topGames,
    mostPopularGame: topGames[0]?.name ?? 'N/A',
    topStories,
  };
};
